import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import {
  AbsoluteFill, Img, continueRender, delayRender,
} from 'remotion';
import { useLayerFont } from '@/hooks/use-layer-font';
import { buildSubtitleTextShadow } from '@/lib/subtitles/subtitleOutline';
import { computeReframeTransform } from '@/lib/layers/computeReframeTransform';
import { wrapQuoteText } from '@/lib/quoteCarousel/quoteTextLayout';
import type { QuoteSlideProps } from '@/lib/quoteCarousel/buildQuoteSlideProps';
import { computeQuoteSlideLayout, quoteFontFamilyCss } from './quoteSlideLayout';
// Inlined as a same-origin data URL rather than a normal asset import/public
// path: this watermark is only ever drawn by the browser (the live Player
// and the browser-side `@remotion/web-renderer` still capture used by
// renderPreviewStillDataUrl.ts), and both the `/images/...` public path and a
// plain asset import get rewritten to cdn.autoclipper.live in production
// (scripts/upload-assets-r2.sh), which serves no CORS headers — the
// renderer's canvas cannot draw a cross-origin image without CORS. See
// watermarkDataUrl.ts for why this is a generated module rather than a
// `?inline` import (Vite 5.4's asset pipeline does not honor `?inline` on a
// plain JS import, only the size-based `assetsInlineLimit`).
import { WATERMARK_DATA_URL } from '@/assets/carousel/watermarkDataUrl';

/** Dark scrim over a card-style frame background so text stays readable. */
const CARD_BACKGROUND_OVERLAY_OPACITY = 0.6;
/** Height of the frame style's readability gradient, fraction of the slide. */
const FRAME_GRADIENT_FRACTION = 0.528;

/**
 * Word-wrapped block height of `text` at `fontSize` and `maxWidth`, measured
 * with `ctx.measureText` (or a rough per-character estimate when `ctx` is
 * null — jsdom's `getContext('2d')` without the optional `canvas` package).
 * A word wider than `maxWidth` counts as several lines (`overflow-wrap:
 * break-word`). `fontCss` is the canvas font WITHOUT the size, e.g.
 * `700 "Bangers", sans-serif`; the slide itself measures through
 * `computeQuoteSlideLayout`, which also accounts for the outline.
 */
export function measureWrappedHeight(
  ctx: CanvasRenderingContext2D | null,
  text: string,
  fontSize: number,
  maxWidth: number,
  lineHeight: number,
  fontCss = 'sans-serif',
): number {
  const { lines } = wrapQuoteText({
    text,
    fontSize,
    maxWidth,
    measureWord: (word, size) => {
      if (!ctx) return word.length * (0.55 * size);
      ctx.font = `${size}px ${fontCss}`;
      return ctx.measureText(word).width;
    },
  });
  return lines * fontSize * lineHeight;
}

export const QuoteSlide: React.FC<QuoteSlideProps> = (props) => {
  const {
    style,
    width,
    height,
    text,
    handle,
    backgroundUrl,
    backgroundColor,
    brand,
    watermark,
    slideIndex,
    slideCount,
    backgroundFrame,
  } = props;
  const fontReady = useLayerFont(brand.fontFamily, brand.fontUrl);

  // The fit measures words in the loaded font, so a still render must wait
  // for the re-measured commit, not just for the font file.
  const [fitHandle] = useState(() => delayRender('quote-slide-font-fit'));
  const fitReleasedRef = useRef(false);
  useEffect(() => {
    if (!fontReady || fitReleasedRef.current) return;
    fitReleasedRef.current = true;
    continueRender(fitHandle);
  }, [fitHandle, fontReady]);

  const layout = useMemo(
    () => computeQuoteSlideLayout(props),
    // `fontReady` flips once the real font is measurable: re-measure then.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props, fontReady],
  );
  const {
    padding, fontSize, outline, quoteMarkSize, block, typography,
  } = layout;
  const fontFamily = quoteFontFamilyCss(brand.fontFamily);

  const textShadow = typography.outline
    ? buildSubtitleTextShadow({
      fontSizePx: fontSize,
      strokeColor: typography.outline.color,
      strokeWidth: typography.outline.widthPct,
      shadowStyle: 'glow',
      shadowColor: null,
      shadowSize: 0,
    })
    : null;

  const reframe = backgroundFrame ? computeReframeTransform(backgroundFrame) : undefined;
  const backgroundNode = backgroundUrl ? (
    <div
      data-testid="quote-slide-background"
      style={{
        position: 'absolute', inset: 0, overflow: 'hidden',
      }}
    >
      <div
        data-testid="quote-slide-background-frame"
        style={{
          position: 'absolute', inset: 0, ...(reframe ?? {}),
        }}
      >
        <Img
          src={backgroundUrl}
          crossOrigin="anonymous"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      </div>
    </div>
  ) : null;

  const alignItems = typography.align === 'left'
    ? 'flex-start'
    : typography.align === 'right' ? 'flex-end' : 'center';

  // One absolutely positioned block (quote mark + text) sized and placed by
  // `computeQuoteTextLayout`, always inside the slide padding. (The old
  // frame layout used an AbsoluteFill with left/right insets, but
  // AbsoluteFill also forces `width: 100%`, which shifted the text box right
  // by the padding and clipped the quote at the right edge.)
  const textBlock = (
    <div
      data-testid="quote-slide-text-block"
      style={{
        position: 'absolute',
        left: block.left,
        top: block.top,
        width: block.width,
        boxSizing: 'border-box',
        padding: `0 ${outline}px`,
        display: 'flex',
        flexDirection: 'column',
        alignItems,
      }}
    >
      {quoteMarkSize > 0 && (
        <div
          style={{
            color: brand.accentColor,
            fontSize: quoteMarkSize,
            height: quoteMarkSize,
            lineHeight: 1,
            fontWeight: 700,
          }}
        >
          &ldquo;
        </div>
      )}
      <div
        data-testid="quote-slide-text"
        style={{
          color: brand.textColor,
          fontSize,
          lineHeight: 1.25,
          textAlign: typography.align,
          fontWeight: typography.bold ? 700 : 400,
          fontStyle: typography.italic ? 'italic' : 'normal',
          textShadow: textShadow ?? undefined,
          width: '100%',
          boxSizing: 'border-box',
          padding: `${outline}px 0`,
          // Last resort alongside the font-aware fit: an unbreakable long
          // token still wraps instead of leaving the box.
          overflowWrap: 'break-word',
        }}
      >
        {text}
      </div>
    </div>
  );

  const watermarkNode = watermark ? (
    // No crossOrigin needed: WATERMARK_DATA_URL is a same-origin `data:` URI
    // (no network fetch), so there is nothing for CORS to block.
    <Img
      src={WATERMARK_DATA_URL}
      style={{
        position: 'absolute',
        right: padding,
        bottom: padding,
        width: width * 0.18,
        opacity: 0.7,
      }}
    />
  ) : null;

  // The watermark owns the bottom-right corner (next to the bottom-left
  // handle), so the "n/N" counter sits top-right in both styles — they used
  // to share the same right/bottom anchor and overlapped.
  const counterNode = (
    <div
      style={{
        position: 'absolute',
        right: padding,
        top: padding * 0.4,
        color: brand.textColor,
        opacity: 0.9,
        fontSize: width * 0.024,
      }}
    >
      {`${slideIndex + 1}/${slideCount}`}
    </div>
  );

  const handleNode = (
    <div
      style={{
        position: 'absolute',
        left: padding,
        bottom: style === 'frame' ? padding * 0.4 : padding,
        color: brand.textColor,
        opacity: 0.9,
        fontSize: width * 0.024,
      }}
    >
      {handle}
    </div>
  );

  // Frame style readability: a dark gradient from the slide edge nearest to
  // the text (bottom by default) that always covers the whole text block.
  const blockCenterY = block.top + block.height / 2;
  const gradientFromTop = blockCenterY < height / 2;
  const gradientHeight = gradientFromTop
    ? Math.max(height * FRAME_GRADIENT_FRACTION, block.top + block.height + padding)
    : Math.max(height * FRAME_GRADIENT_FRACTION, height - block.top + padding);
  const gradientNode = (
    <div
      data-testid="quote-slide-gradient"
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        height: Math.min(height, gradientHeight),
        ...(gradientFromTop ? { top: 0 } : { bottom: 0 }),
        background: `linear-gradient(${gradientFromTop ? 'to bottom' : 'to top'}, rgba(0,0,0,0.85), transparent)`,
      }}
    />
  );

  return (
    <AbsoluteFill style={{ backgroundColor, fontFamily }}>
      {backgroundNode}
      {style === 'frame' && gradientNode}
      {style === 'card' && backgroundUrl ? (
        // A per-slide captured frame replaces the flat card color; the dark
        // overlay keeps the brand text readable over any footage.
        <div
          data-testid="quote-slide-card-overlay"
          style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: `rgba(0, 0, 0, ${CARD_BACKGROUND_OVERLAY_OPACITY})`,
          }}
        />
      ) : null}
      {textBlock}
      {handleNode}
      {counterNode}
      {watermarkNode}
    </AbsoluteFill>
  );
};
